import ScButton from "@/src/components/ui-kit/button";
import InputField from "@/src/components/ui-kit/input-field.rhf";
import type { Category } from "@/src/models/category.model";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

type EditCategoryModalProps = Readonly<{
  visible: boolean;
  category: Category | null;
  onSave: (categoryId: string, categoryName: string) => void;
  onCancel: () => void;
  loading?: boolean;
}>;

type EditCategoryFormValues = Readonly<{
  categoryName: string;
}>;

const CATEGORY_MAX_LENGTH = 20;

const EditCategoryModal = ({
  visible,
  category,
  onSave,
  onCancel,
  loading = false,
}: EditCategoryModalProps) => {
  const { t } = useTranslation();
  const { varStyle } = useTheme();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EditCategoryFormValues>({
    values: { categoryName: category?.categoryName ?? "" },
  });

  const categoryNameValue = watch("categoryName");
  const isOverLimit = categoryNameValue.length > CATEGORY_MAX_LENGTH;
  const isSaveDisabled = isOverLimit || !categoryNameValue.trim();

  const errorMessage = isOverLimit
    ? `${t("ERROR_MAX_LENGTH")} ${CATEGORY_MAX_LENGTH}`
    : errors.categoryName?.message;

  const handleSave = (formValues: EditCategoryFormValues) => {
    const trimmedName = formValues.categoryName.trim();
    if (!trimmedName || isOverLimit || !category) return;
    onSave(category.ItemId, trimmedName);
    reset();
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={handleCancel}
    >
      <Pressable
        onPress={handleCancel}
        style={varStyle}
        className="flex-1 justify-center items-center bg-black/50"
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="bg-chalk rounded-2xl p-6 w-4/5 max-w-md gap-4"
        >
          <Text className="text-xl font-semibold text-onyx">
            {t("LABEL_EDIT_CATEGORY")}
          </Text>

          <InputField
            control={control}
            name="categoryName"
            label={t("LABEL_CATEGORY_NAME")}
            placeholder={t("HINT_ENTER_CATEGORY_NAME")}
            error={errorMessage}
          />

          <View className="flex-row gap-3">
            <View className="flex-1">
              <ScButton
                text="BTN_CANCEL"
                mode="outlined"
                onPress={handleCancel}
              />
            </View>
            <View className="flex-1">
              <ScButton
                text="BTN_SAVE"
                mode="contained"
                disabled={isSaveDisabled}
                onPress={handleSubmit(handleSave)}
                loading={loading}
              />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default EditCategoryModal;
